import * as Yup from 'yup';
import { isValidCPF, isValidCNPJ, isValidCEP, isValidPhone } from './brazilianFields';

/**
 * Yup custom validators for Brazilian fields
 */

/**
 * CPF validator
 */
export const cpfValidator = Yup.string()
  .test('valid-cpf', 'CPF inválido', (value) => {
    if (!value) return true; // Let required() handle empty values
    return isValidCPF(value);
  });

/**
 * Required CPF validator
 */
export const cpfRequired = cpfValidator.required('CPF é obrigatório');

/**
 * CNPJ validator
 */
export const cnpjValidator = Yup.string()
  .test('valid-cnpj', 'CNPJ inválido', (value) => {
    if (!value) return true;
    return isValidCNPJ(value);
  });

/**
 * Required CNPJ validator
 */
export const cnpjRequired = cnpjValidator.required('CNPJ é obrigatório');

/**
 * CEP validator
 */
export const cepValidator = Yup.string()
  .test('valid-cep', 'CEP inválido (deve ter 8 dígitos)', (value) => {
    if (!value) return true;
    return isValidCEP(value);
  });

/**
 * Required CEP validator
 */
export const cepRequired = cepValidator.required('CEP é obrigatório');

/**
 * Phone validator
 */
export const phoneValidator = Yup.string()
  .test('valid-phone', 'Telefone inválido (deve ter 10 ou 11 dígitos)', (value) => {
    if (!value) return true;
    return isValidPhone(value);
  });

/**
 * Required phone validator
 */
export const phoneRequired = phoneValidator.required('Telefone é obrigatório');

/**
 * Email validator (Portuguese messages)
 */
export const emailValidator = Yup.string()
  .email('Email inválido');

/**
 * Required email validator
 */
export const emailRequired = emailValidator.required('Email é obrigatório');

/**
 * Birth date validator - Validates that the person is at least 18 years old and not older than 110 years
 */
export const birthDateValidator = (value: string | undefined): boolean => {
  if (!value) return false;

  const birthDate = new Date(value);
  const today = new Date();

  // Calculate age
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();

  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }

  // Age must be between 18 and 110 years
  return age >= 18 && age <= 110;
};

/**
 * Create partner validation schema (for Phase 1)
 */
export const createPartnerSchema = (isFirst: boolean) => Yup.object().shape({
  name: Yup.string()
    .required('Nome é obrigatório')
    .min(3, 'Nome deve ter no mínimo 3 caracteres'),
  cpf: cpfRequired, // CPF is now required for all partners
  rg: Yup.string()
    .optional(),
  dataNascimento: Yup.string()
    .required('Data de Nascimento é obrigatória')
    .test('valid-birth-date', 'A pessoa deve ter entre 18 e 110 anos', (value) => {
      if (!value) return false;
      return birthDateValidator(value);
    }),
  senhaGov: Yup.string()
    .required('Senha Gov.br é obrigatória'),
  phone: phoneRequired, // Phone is ALWAYS required for all partners
  email: emailValidator.optional(),
  entidadeClasse: Yup.string().optional(),
  address: Yup.object().shape({
    street: Yup.string(),
    number: Yup.string().when('cep', {
      is: (cep: string) => cep && cep.length > 0,
      then: (schema) => schema.required('Número é obrigatório'),
      otherwise: (schema) => schema.optional(),
    }),
    complement: Yup.string(),
    neighborhood: Yup.string(),
    city: Yup.string(),
    state: Yup.string().length(2, 'Estado deve ter 2 caracteres (UF)'),
    cep: cepValidator,
  }).optional().nullable(),
});

/**
 * Phase 1 validation schema (Partners only)
 */
export const phase1ValidationSchema = Yup.object().shape({
  partners: Yup.array()
    .of(createPartnerSchema(false))
    .min(1, 'Ao menos um sócio é obrigatório')
    .max(3, 'Máximo de 3 sócios permitido')
    .test('first-partner-required', 'O primeiro sócio deve ter todos os campos obrigatórios preenchidos', function(partners) {
      if (!partners || partners.length === 0) return false;
      const firstPartner = partners[0];
      return !!(firstPartner.name && firstPartner.cpf && firstPartner.phone);
    }),
});

/**
 * Phase 2 validation schema (Company Basic Info + Additional Details)
 */
export const phase2ValidationSchema = Yup.object().shape({
  name: Yup.string()
    .required('Razão Social é obrigatória')
    .min(3, 'Razão Social deve ter no mínimo 3 caracteres'),
  cnpj: cnpjRequired,
  taxRegime: Yup.number()
    .required('Regime Tributário é obrigatório')
    .oneOf([1, 2, 3], 'Regime Tributário inválido'),
  companyType: Yup.number()
    .optional()
    .oneOf([1, 2, 3, 4, 5], 'Tipo Empresarial inválido'),
  cnaePrincipal: Yup.string()
    .required('CNAE Principal é obrigatório'),
  cnaeSecondary: Yup.string().optional(),
  nire: Yup.string()
    .optional()
    .max(20, 'NIRE não pode exceder 20 caracteres'),
  simplesCode: Yup.string()
    .optional()
    .max(20, 'Código do Simples não pode exceder 20 caracteres'),
  professionalEntity: Yup.string()
    .optional()
    .max(100, 'Entidade de Classe não pode exceder 100 caracteres'),
  anexo: Yup.string()
    .when('taxRegime', {
      is: 1, // Simples Nacional
      then: (schema) => schema.required('Anexo do Simples Nacional é obrigatório'),
      otherwise: (schema) => schema.optional(),
    }),
  // Segmento is now required for all tax regimes (matching backend validation)
  segmento: Yup.string()
    .required('Segmento é obrigatório')
    .max(50, 'Segmento não pode exceder 50 caracteres'),
  ramoAtividadeId: Yup.string()
    .required('Ramo de Atividade é obrigatório'),
  stateRegistration: Yup.string()
    .optional()
    .max(20, 'Inscrição Estadual não pode exceder 20 caracteres'),
  municipalRegistration: Yup.string()
    .optional()
    .max(20, 'Inscrição Municipal não pode exceder 20 caracteres'),
  email: emailValidator.optional(),
  phone: phoneValidator.optional(),
  headquartersAddress: Yup.object().shape({
    street: Yup.string(),
    number: Yup.string().when('cep', {
      is: (cep: string) => cep && cep.length > 0,
      then: (schema) => schema.required('Número é obrigatório'),
      otherwise: (schema) => schema.optional(),
    }),
    complement: Yup.string(),
    neighborhood: Yup.string(),
    city: Yup.string(),
    state: Yup.string().length(2, 'Estado deve ter 2 caracteres (UF)'),
    cep: cepValidator,
  }).optional().nullable(),
  employeeRange: Yup.number()
    .optional()
    .oneOf([1, 2, 3, 4, 5, 6, 7, 8, 9, 10], 'Faixa de funcionários inválida'),
  invoicePassword: Yup.string().optional(),
  invoiceEmissionPortalUrl: Yup.string()
    .optional()
    .url('URL inválida. Use o formato: https://exemplo.com'),
});

/**
 * Phase 3 validation schema (Certificates)
 */
export const phase3ValidationSchema = Yup.object().shape({
  ecpfKey: Yup.string().optional(),
  ecpfPassword: Yup.string()
    .when('ecpfKey', {
      is: (val: string) => val && val.length > 0,
      then: (schema) => schema.required('Senha e-CPF é obrigatória quando a chave é fornecida'),
      otherwise: (schema) => schema.optional(),
    }),
  ecnpjKey: Yup.string().optional(),
  ecnpjPassword: Yup.string()
    .when('ecnpjKey', {
      is: (val: string) => val && val.length > 0,
      then: (schema) => schema.required('Senha e-CNPJ é obrigatória quando a chave é fornecida'),
      otherwise: (schema) => schema.optional(),
    }),
});
